import { initialState, useFundItPage } from "./FundItPageContext";
import progressData from "./fundItProgressData";
import { mockData } from "./mockData";

export const getMissingDataForSignup = (data) => {
  const properties = [
    "addressInUS",
    "hasAddress",
    "isAddressValid",
    "hasDenomination",
    "hasWebsite",
    "userEmailVerified",
    "userHasFirstName",
    "userHasLastName",
    "userPhoneVerified",
  ];
  return properties.filter((prop) => !data[prop]);
};

export function useFundItSignupProgress(fundItStatusData) {
  if (!fundItStatusData) return {};

  const {
    userIsSuperAdmin,
    paymentsEnabled,
    canGetPayouts,
    hasMerchantAccount,
    canGetPayments,
    numberOfFunds,
    hasEnteredKYC,
    hasEnteredBankInfo,
    hasKYCValidationError,
    networkIsExpired,
    networkHasRequestedReactivation,
    wepayHasReactivatedNetwork,
    ...rest
  } = fundItStatusData;

  const missingDataForSignup = getMissingDataForSignup(rest);

  if (!userIsSuperAdmin || !paymentsEnabled)
    return { ...progressData.noPermissions, fundItStatusData };
  else if (canGetPayouts)
    return { ...progressData.setupComplete, fundItStatusData };
  else if (networkHasRequestedReactivation && !wepayHasReactivatedNetwork)
    return { ...progressData.awaitingReactivation, fundItStatusData };
  else if (networkIsExpired && !wepayHasReactivatedNetwork)
    return { ...progressData.needTosAgreement, fundItStatusData };
  else if (!hasMerchantAccount)
    if (missingDataForSignup.length)
      return {
        ...progressData.missingDataForSignup,
        data: missingDataForSignup,
        fundItStatusData,
      };
    else return { ...progressData.needTosAgreement, fundItStatusData };
  else if (!hasEnteredKYC)
    return { ...progressData.needToEnterKYC, fundItStatusData };
  else if (!hasEnteredBankInfo)
    return { ...progressData.needToEnterBankInfo, fundItStatusData };
  else if (hasKYCValidationError)
    return { ...progressData.hasKYCValidationError, fundItStatusData };
  else return { ...progressData.waitingForKYCApproval };
}

export function useFundItSignupProgressForFundItPage() {
  const { state } = useFundItPage();
  return useFundItSignupProgress(state);
}

// ######################
// MOCK
// to use this, set the `forceProgressState` variable in mockData file
// ######################
export function useMockFundItSignupProgress() {
  const { forceProgressState } = mockData;
  const { state } = useFundItPage();
  const { pricing, termsOfService } = state;

  if (!forceProgressState || !(forceProgressState in progressData)) return null;

  const hasAllDataForSignup = {
    addressInUS: true,
    hasAddress: true,
    hasDenomination: true,
    hasWebsite: true,
    userEmailVerified: true,
    userHasFirstName: true,
    userHasLastName: true,
    userPhoneVerified: true,
  };
  let forcedState = {
    ...initialState,
    pricing,
    termsOfService,
    needInitialFetch: false,
  };

  switch (forceProgressState) {
    case "noPermissions":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: false,
      };
      break;
    case "dashboard":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        canGetPayouts: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
        hasEnteredKYC: true,
        hasKYCValidationError: false,
      };
      break;
    case "missingDataForSignup":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
      };
      break;
    case "needTosAgreement":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
      };
      break;
    case "needToCreateFund":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
      };
      break;
    case "needToEnterKYC":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
      };
      break;
    case "creatingAccount":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
        hasEnteredKYC: true,
      };
      break;
    case "needToEnterBankInfo":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
        hasEnteredKYC: true,
        canGetPayments: true,
      };
      break;
    case "hasKYCValidationError":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
        hasEnteredBankInfo: true,
        hasEnteredKYC: true,
        canGetPayments: true,
        hasKYCValidationError: true,
      };
      break;
    case "waitingForKYCApproval":
      forcedState = {
        ...forcedState,
        userIsSuperAdmin: true,
        paymentsEnabled: true,
        ...hasAllDataForSignup,
        hasMerchantAccount: true,
        numberOfFunds: 2,
        hasEnteredBankInfo: true,
        hasEnteredKYC: true,
        canGetPayments: true,
        hasKYCValidationError: false,
      };
      break;
    default:
      break;
  }

  return forcedState;
}
